/**
 * ONE derivation of "which of the three orders a guest has is this".
 *
 * A guest's order is three different products wearing one status enum, and the
 * order page served all three from one 646-line component (founder decision,
 * 2026-08-24):
 *
 *   1. A MORNING BOOKED. Nothing is happening and nothing will until the
 *      counter starts. What the guest wants is to check what they ordered, and
 *      to be able to call it off while that is still free.
 *   2. AN ORDER IN PROGRESS. Something is happening right now and the page has
 *      to say what: the counter, the courier, the map, the arrival time. This
 *      is the only one that moves under the reader, and the only one worth
 *      polling.
 *   3. A RECEIPT. It is over. The guest wants the document, the proof, and a
 *      way to complain.
 *
 * Sharing one component meant every change to one of them was a change to the
 * other two, and the three kept acquiring each other's furniture - a receipt
 * button on an order still being baked, a cancel control on a delivered one, a
 * tracker on a morning three days away. The split is the point of this table:
 * the LIVE view is its own file and the other two share one, because those two
 * differ by conditions (can it still be cancelled, is there a receipt yet)
 * while the live one differs by being an entirely different screen.
 *
 * The line between PLANNED and LIVE is the counter STARTING, not the counter
 * ACCEPTING. An order confirmed tonight for tomorrow at 07:00 has been
 * committed to and can no longer be cancelled, but nothing is in progress and a
 * progress view would be an empty promise all night. Cancellability is a
 * separate question the server answers (`cancellable`, GST-009), which is why
 * the planned view asks it rather than assuming it - the bucket spans both
 * sides of acceptance.
 *
 * Pure, exhaustive over `OrderStatus`, and stated over wire types like every
 * other projection here: a new status is a typecheck failure rather than a
 * silent fall-through into the wrong screen.
 */

import type { DeliveryPhaseKind, OrderPhase, PaymentPhase } from '../enums';
import { deliveryUnbooked } from './delivery-phase';
import { orderAcceptedByCounter, orderStopped } from './order-phase';

/** The regions, as much of them as these three questions read. */
export interface GuestOrderViewInput {
  readonly payment: PaymentPhase;
  readonly order: OrderPhase;
  readonly delivery: DeliveryPhaseKind;
}

export type GuestOrderView =
  /** A morning booked. Nothing in progress; cancellable while the server says so. */
  | 'PLANNED'
  /** In the pipe: the counter has started, and something changes under the reader. */
  | 'LIVE'
  /** Over, one way or another - delivered, refused, cancelled, failed. */
  | 'SETTLED';

/**
 * The table. Every `OrderStatus` appears exactly once; the compiler enforces
 * that, which is the entire point of a `Record` over a set of arrays that can
 * each forget a different member - and one of them did. See `guestOrderMoves`.
 */
/**
 * Which of the three screens this order belongs on.
 *
 * This was a twenty-four row table over `OrderStatus`. It is three sentences
 * about the regions now, and the sentences are the actual product rule: an
 * order is SETTLED when it is over, LIVE once the counter has started making it
 * or a courier is engaged, and PLANNED before that.
 *
 * The endings come first and the workflow last, which is the order the sentences
 * are in. The switch at the bottom is over what the endings LEFT, so a new
 * `OrderPhase` has to be placed on one side of the live line by hand rather than
 * inheriting whatever the last arm said.
 */
export function guestOrderViewOf(order: GuestOrderViewInput): GuestOrderView {
  if (orderStopped(order.order)) return 'SETTLED';
  // No money was taken, so nothing is coming. A refund is the refund case's
  // and never settles an order on its own (register item 51).
  if (order.payment === 'FAILED') return 'SETTLED';
  if (order.delivery === 'COMPLETED' || order.delivery === 'FAILED') return 'SETTLED';
  /*
   * A job that came to nothing does NOT settle an open order (#2004, approved
   * 2026-10-08). `NO_COURIER` on an order still running is a live courier
   * search - the platform is re-booking it (RTE-035) - and the guest's orders
   * list reads it as Ready, "Finding a courier" on the tracker
   * (`guestStageOf`). This line used to answer SETTLED here, so the list said a
   * stage while the page it opened showed no tracker at all. When the search
   * gives up the ORDER is cancelled, and `orderStopped` above settles it.
   */
  switch (order.order) {
    // The counter has started. There is something happening to report.
    case 'PREPARING':
    case 'READY':
    case 'COLLECTED':
    case 'DELIVERED':
      return 'LIVE';
    /*
     * Nothing has been made. A courier engaged makes the order live anyway, on
     * an immediate order. A route PRICED does not, and that distinction is the
     * whole of `deliveryUnbooked`: the estimate is taken at allocation, so
     * `delivery !== 'NOT_BOOKED'` would have sent every order there is - a
     * morning booked for Saturday included - to the live tracker at the moment
     * it was placed, to watch a courier nobody had asked for.
     */
    case 'CREATED':
    case 'SCHEDULED':
    case 'ACCEPTANCE_OPEN':
    case 'ACCEPTANCE_CALLING':
    /*
     * Refused, and waiting for the engine to offer it again (#2155): an
     * offer by another name. A HANDED-ON order - one a closing house had
     * accepted - never reaches this arm, because the guest's wire presents it
     * at the stage it had reached (decision 11).
     */
    case 'AWAITING_ASSIGNMENT':
      return deliveryUnbooked(order.delivery) ? 'PLANNED' : 'LIVE';
  }
}

/**
 * True while the order can still change under the guest - the one question
 * polling should ask.
 *
 * It was asked as a literal array of seven "settled" statuses in the guest
 * app's query hook, and that array was missing `UNALLOCATED`,
 * `COURIER_UNAVAILABLE` and `MANUAL_INTERVENTION` - so an order the platform
 * had given up on polled the API every few seconds for as long as the tab
 * stayed open, and an order in manual intervention polled for ever. Asking the
 * table instead makes a forgotten status impossible: a settled order is one
 * this record calls SETTLED, and nothing else.
 */
export function guestOrderMoves(order: GuestOrderViewInput): boolean {
  return guestOrderViewOf(order) !== 'SETTLED';
}

/**
 * Has a counter TAKEN this order - and is it still going?
 *
 * A different question from `guestOrderViewOf`, and deliberately a second
 * table rather than a set of statuses read off the first one. The view splits
 * on the counter STARTING; this splits on the counter ACCEPTING, which happens
 * earlier and which is the moment the order stops being a booking somebody
 * might still refuse and becomes somebody's work. `CONFIRMED` sits on the
 * planned side of one line and the accepted side of this one, so neither table
 * can be derived from the other.
 *
 * The guest orders list is what asks: a row for this morning says "Today"
 * while nothing has been committed to it, and "In progress" once a bakery has
 * (founder decision, 2026-08-24). A row already being prepared or carried says
 * so in its own words and never reaches this question.
 *
 * `orderAcceptedByCounter` is the whole of it, and the right question here,
 * because it asks whether the counter has COMMITTED and not what it has done
 * since.
 */
export function guestOrderAccepted(order: GuestOrderViewInput): boolean {
  return orderAcceptedByCounter(order.order);
}
